import { PlusIcon, XMarkIcon } from "@heroicons/react/16/solid";
import { message } from "antd";
import { useContext, useState } from "react";
import { api } from "../../../api";
import { OverflowFrame } from "../../../externalComponents";
import { PageHeading } from "../../../externalComponents/PageHeading";
import { SettingsSidebar } from "../../../externalComponents/SettingsSidebar";
import { Badge } from "../../../externalComponents/tailwind/badge";
import { Button } from "../../../externalComponents/tailwind/button";
import {
  Description,
  Field,
  Fieldset,
} from "../../../externalComponents/tailwind/fieldset";
import { Heading } from "../../../externalComponents/tailwind/heading";
import { Input } from "../../../externalComponents/tailwind/input";
import { CustomerContext } from "../../../providers";

export default function CustomFieldsPage() {
  const { customer } = useContext(CustomerContext);

  const [newCustomField, setNewCustomField] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const handleAddCustomField = async () => {
    setIsLoading(true);
    let newValue = newCustomField?.toLowerCase()?.replaceAll(" ", "");
    let oldCustomFields = customer.customFields;
    let newCustomFields;
    if (!oldCustomFields) {
      newCustomFields = {
        [newValue]: true,
      };
    } else {
      newCustomFields = { ...oldCustomFields };
      newCustomFields[newValue] = true;
    }

    try {
      await api.customer.update(customer.id, {
        customFields: newCustomFields,
      });
      setNewCustomField("");
      message.success(`Custom field ${newValue} has been added.`);
    } catch (e) {
      message.error(`Custom field ${newValue} cannot be added. Error: ${e}`);
    }
    setIsLoading(false);
  };

  const handleDeleteCustomField = async (fieldToDelete) => {
    setIsDeleteLoading(true);
    message.loading(`Deleting custom field ${fieldToDelete}...`);

    let oldCustomFields = customer.customFields;
    let newCustomFields;

    if (oldCustomFields && oldCustomFields[fieldToDelete]) {
      newCustomFields = { ...oldCustomFields };
      delete newCustomFields[fieldToDelete];

      (async () => {
        try {
          await api.enqueue(
            api.removeCustomFieldOfButton,
            {
              fieldToDelete,
            },
            {
              queue: {
                name: "custom-field-deletion",
                maxConcurrency: 10,
              },
            },
          );
        } catch (e) {
          message.error(
            `Error while trying to delete custom field ${fieldToDelete} from buttons and list. Error: ${e}`,
          );
        }

        try {
          await api.customer.update(customer.id, {
            customFields: newCustomFields,
          });

          message.success(`Custom field ${fieldToDelete} has been deleted.`);
        } catch (e) {
          message.error(
            `Custom field ${fieldToDelete} cannot be deleted. Error: ${e}`,
          );
        }
      })();
    } else {
      message.error(`Custom field ${fieldToDelete} does not exist.`);
    }

    setIsDeleteLoading(false);
  };

  const handleKeyUp = (e) => {
    if (e.key === "Enter") {
      handleAddCustomField(e.target.value);
    }
  };
  return (
    <>
      <SettingsSidebar>
        <PageHeading title="Custom Fields" />
        <OverflowFrame>
          <div className="mt-5 w-full">
            <Fieldset>
              <Field>
                <Description>
                  Custom Fields are personalized properties attached to a
                  contact. You can define them from a CSV import or an API call.
                  You can then insert them into variables, whether in templates
                  or WhatsApp messages.
                </Description>
                <br />
                <Description>
                  {`For example, you can send a specific “discountcode” for each contact, then insert it in a message with {{discountcode}} or in a template by selecting it from the list of parameters.`}
                </Description>
              </Field>
              {customer.customFields && (
                <>
                  <Heading className="mt-5">Your custom fields</Heading>
                  <div className="flex w-full flex-wrap">
                    {Object.keys(customer.customFields)
                      .filter(Boolean)
                      .map((field, index) => (
                        <div
                          className="relative group"
                          key={`custom-field-${index}`}
                        >
                          <Badge
                            key={index}
                            className="mt-4 mr-4 cursor-default"
                          >
                            <div className="p-1">{field}</div>
                          </Badge>
                          <div
                            key={`delete-${index}`}
                            className="border rounded-full h-4 w-4 flex items-center justify-center text-gray-400 absolute bottom-5 right-2 bg-white opacity-0 group-hover:opacity-100 hover:opacity-100 hover:cursor-pointer transition-opacity duration-200"
                            onClick={() => handleDeleteCustomField(field)}
                          >
                            {isDeleteLoading && (
                              <div
                                className={`small-loader border-r-zinc-200 bg-white z-10 w-4`}
                              ></div>
                            )}
                            {!isDeleteLoading && <XMarkIcon />}
                          </div>
                        </div>
                      ))}
                  </div>
                </>
              )}
              <Field>
                <div className="flex mt-5">
                  <Input
                    className="mr-10"
                    name="Custom field input"
                    placeholder="new_custom_field"
                    value={newCustomField}
                    onChange={(e) =>
                      setNewCustomField(
                        e.target.value?.toLowerCase()?.replaceAll(" ", ""),
                      )
                    }
                    maxLength={64}
                    autoComplete="off"
                    onKeyUp={(e) => {
                      handleKeyUp(e);
                    }}
                  />

                  <Button
                    className="h-9"
                    color="primaryButton"
                    onClick={handleAddCustomField}
                    loading={isLoading}
                  >
                    <PlusIcon />
                    Add
                  </Button>
                </div>
              </Field>
            </Fieldset>
          </div>
        </OverflowFrame>
      </SettingsSidebar>
    </>
  );
}
